import { detectDeviceCapabilities } from './deviceTier';

/**
 * Compress an image blob for upload based on device tier.
 * Tier D gets aggressive compression (800px max, JPEG 0.8).
 * Tier C gets moderate compression (1024px max, JPEG 0.9).
 * Tier A/B pass through with minimal changes.
 */

export async function compressForUpload(file: File): Promise<File> {
  const caps = detectDeviceCapabilities();

  let maxDimension: number;
  let quality: number;

  switch (caps.tier) {
    case 'D':
      maxDimension = 800;
      quality = 0.8;
      break;
    case 'C':
      maxDimension = 1024;
      quality = 0.9;
      break;
    default:
      // Tier A/B: already preprocessed to 1024, minimal extra compression
      maxDimension = 1024;
      quality = 0.92;
      break;
  }

  let img: ImageBitmap;
  try {
    img = await createImageBitmap(file);
  } catch {
    throw new Error('Failed to decode image for compression. The file may be corrupt.');
  }

  let { width, height } = img;
  const maxDim = Math.max(width, height);

  if (maxDim > maxDimension) {
    const scale = maxDimension / maxDim;
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, width, height);

  img.close?.();

  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => {
      if (!b) {
        reject(new Error('Canvas export failed during compression.'));
        return;
      }
      resolve(b);
    }, 'image/jpeg', quality)
  );

  return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
